import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { isPersonAlreadyOnClientError } from '../lib/is-person-already-on-client-error';
import { resolveEmailInUseError } from '../lib/resolve-email-in-use-error';
import type {
    AddClientContactInput,
    AttachClientContactInput,
    CreateClientInput,
    UpdateClientProfileInput,
    UpdateContactProfileInput,
} from '../model/client.types';
import { clientsApi } from './client.api';
import { queryKeys } from './client.queries';

export const mutationKeys = {
    all: ['client'] as const,
    addContact: (clientId: string) => [...mutationKeys.all, 'add-contact', clientId] as const,
    attachContact: (clientId: string) => [...mutationKeys.all, 'attach-contact', clientId] as const,
    updateContactProfile: (personId: string) =>
        [...mutationKeys.all, 'update-contact-profile', personId] as const,
    updateProfile: (clientId: string) => [...mutationKeys.all, 'update-profile', clientId] as const,
};

export function useAddClientContactOptions(clientId: string) {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.addContact(clientId),
        mutationFn: (command: AddClientContactInput) => clientsApi.addContact(clientId, command),
        onSuccess: async () => {
            // Only the contacts list: `queryKeys.all` would also refetch the profile, and the chart
            // swaps its whole body for a spinner while that is in flight.
            // The new person is a candidate for every other client's Existing contact search.
            queryClient.invalidateQueries({ queryKey: queryKeys.contactCandidatesOfAnyClient() });
            await queryClient.invalidateQueries({ queryKey: queryKeys.contacts(clientId) });
            toast.success('Contact added');
        },
        onError: (error) => {
            // Shown inline on the email field by the form, which has the field to put it on.
            if (resolveEmailInUseError(error)) return;

            console.error(error);
            toast.error('Failed to add contact');
        },
    });
}

export function useAddClientPracticeMemberOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (clientId: string) => clientsApi.addPracticeMember(clientId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.all }),
        onError: (error) => {
            console.error(error);
            toast.error('Failed to add existing client');
        },
    });
}

export function useAttachClientContactOptions(clientId: string) {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.attachContact(clientId),
        mutationFn: (command: AttachClientContactInput) =>
            clientsApi.attachContact(clientId, command),
        onSuccess: async (_, command) => {
            // Never the opened client's profile: refetching it swaps the chart for a spinner (see
            // `useAddClientContactOptions`). Saving rewrote the person's shared details (FR-024), so
            // every other client's cached contacts and profile are marked stale without a refetch,
            // and this client's search must stop offering the person it now has. The person's own
            // contact profile and every Relationships section (the person may be a client too) are
            // marked stale the same way.
            queryClient.invalidateQueries({
                queryKey: queryKeys.profilesOfAnyClient(),
                refetchType: 'none',
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.relationshipLists(),
                refetchType: 'none',
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.contactProfile(command.personId),
                refetchType: 'none',
            });
            queryClient.invalidateQueries({ queryKey: queryKeys.person(command.personId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.contactCandidatesOfAnyClient() });
            await queryClient.invalidateQueries({ queryKey: queryKeys.contactsOfAnyClient() });
            toast.success('Contact added');
        },
        onError: (error) => {
            // Both are shown by the form, which has the field or the selection to put them on.
            if (resolveEmailInUseError(error) || isPersonAlreadyOnClientError(error)) {
                return;
            }

            console.error(error);
            toast.error('Failed to add contact');
        },
    });
}

export function useCreateClientOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (command: CreateClientInput) => clientsApi.create(command),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.all });
            toast.success('Client created');
        },
        onError: (error) => {
            if (resolveEmailInUseError(error)) return;
            console.error(error);
            toast.error('Failed to create client');
        },
    });
}

export function useUpdateContactProfileOptions(personId: string) {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.updateContactProfile(personId),
        mutationFn: (command: UpdateContactProfileInput) =>
            clientsApi.updateContactProfile(command),
        onSuccess: async () => {
            // The Add contact search and the attach form's person read show this contact's name and
            // email, so they are marked stale too — not awaited, nothing here waits on them.
            queryClient.invalidateQueries({ queryKey: queryKeys.contactCandidatesOfAnyClient() });
            queryClient.invalidateQueries({ queryKey: queryKeys.person(personId) });
            // By prefix, not by the clients this contact is on: a chart that is not open still
            // caches its Contacts list, and would show the old details when it is opened next.
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: queryKeys.contactProfile(personId) }),
                queryClient.invalidateQueries({ queryKey: queryKeys.contactsOfAnyClient() }),
            ]);
            toast.success('Contact updated');
        },
        onError: (error) => {
            // Shown inline on the email field by the form, as on the client edit.
            if (resolveEmailInUseError(error)) return;
            console.error(error);
            toast.error('Failed to update contact');
        },
    });
}

export function useUpdateClientProfileOptions(clientId: string) {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.updateProfile(clientId),
        mutationFn: (command: UpdateClientProfileInput) => clientsApi.updateProfile(command),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.all });
            toast.success('Profile updated');
        },
        onError: (error) => {
            if (resolveEmailInUseError(error)) return;
            console.error(error);
            toast.error('Failed to update profile');
        },
    });
}
